import { useState, useRef } from "react";
import { FileText, ArrowRight, Copy, Check, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const EXAMPLE_FORMULAS = [
  '=VLOOKUP(A2,Sheet2!A:B,2,FALSE)',
  '=IF(AND(B2>100,C2="Yes"),B2*0.1,0)',
  '=SUMIFS(D:D,A:A,"Sales",B:B,">1000")',
  '=INDEX(B2:B10,MATCH(MAX(C2:C10),C2:C10,0))',
  '=IFERROR(VLOOKUP(A1,Table1,3,0),"Not Found")',
];

export default function FormulaExplainer() {
  const [formula, setFormula] = useState("");
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  const handleExplain = async () => {
    if (!formula.trim()) return;
    setLoading(true);
    setError("");
    setExplanation("");

    try {
      const res = await axios.post(`${API}/formula/explain`, { formula: formula.trim() });
      setExplanation(res.data.explanation);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(explanation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setFormula("");
    setExplanation("");
    setError("");
    textareaRef.current?.focus();
  };

  const handleExample = (ex) => {
    setFormula(ex);
    setExplanation("");
    setError("");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#0055FF]" />
            </div>
            <span className="label-mono">Formula Explainer</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="formula-page-title"
          >
            Explain any Excel formula
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Paste a formula from Excel or Google Sheets, and get a simple, plain-English explanation instantly.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Input */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <label className="label-mono block mb-3">Your Formula</label>
              <Textarea
                ref={textareaRef}
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder='e.g. =VLOOKUP(A2,Sheet2!A:B,2,FALSE)'
                className="min-h-[140px] font-mono text-sm bg-slate-50 border-transparent focus:border-[#0055FF] focus:bg-white rounded-md resize-none"
                data-testid="formula-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleExplain}
                disabled={loading || !formula.trim()}
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-md px-6 h-11 font-medium btn-press"
                data-testid="explain-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Explaining...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Explain Formula
                  </>
                )}
              </Button>
              {(formula || explanation) && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press"
                  data-testid="reset-button"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Reset
                </Button>
              )}
            </div>

            {/* Example formulas */}
            <div>
              <span className="label-mono block mb-3">Try an Example</span>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_FORMULAS.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => handleExample(ex)}
                    className="text-xs font-mono px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors btn-press"
                    data-testid={`example-formula-${i}`}
                  >
                    {ex.length > 35 ? ex.slice(0, 35) + '...' : ex}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Output */}
          <div className="lg:col-span-5">
            <div className="border border-slate-200 rounded-lg bg-white min-h-[300px] relative">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="label-mono">Explanation</span>
                {explanation && (
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0055FF] transition-colors"
                    data-testid="copy-explanation-button"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
              <div className="p-5" data-testid="explanation-output">
                {loading && (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Analyzing formula...</span>
                  </div>
                )}
                {error && (
                  <p className="text-sm text-red-500" data-testid="formula-error">{error}</p>
                )}
                {!loading && !error && !explanation && (
                  <div className="text-sm text-slate-400 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Paste a formula and click "Explain Formula" to see the breakdown here.
                  </div>
                )}
                {explanation && (
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {explanation}
                  </div>
                )}
              </div>
            </div>

            {/* Ad */}
            <div className="mt-6">
              <AdUnit format="vertical" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
